import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  CheckCircle,
  AlertCircle,
  Loader2,
  CreditCard,
  Briefcase,
  Home
} from 'lucide-react';
import { toast } from 'sonner';

function PaymentSuccessPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const maxPollingAttempts = 5;
  const pollingInterval = 2000; // 2 seconds

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setLoading(false);
      setPaymentStatus({ error: 'No session ID provided' });
    }
  }, [sessionId]);

  const checkPaymentStatus = async (sessionId, attempt = 0) => {
    try {
      const response = await axios.get(`/payments/status/${sessionId}`);
      const status = response.data;
      
      if (status.payment_status === 'paid') {
        setPaymentStatus({
          success: true,
          status: status.status,
          payment_status: status.payment_status,
          amount: status.amount_total / 100, // Convert from cents
          currency: status.currency.toUpperCase()
        });
        setLoading(false);
        toast.success('Payment successful!');
        return;
      } else if (status.status === 'expired') {
        setPaymentStatus({
          success: false,
          error: 'Payment session expired',
          status: status.status
        });
        setLoading(false);
        return;
      }
      
      // If payment is still pending and we haven't reached max attempts, poll again
      if (attempt < maxPollingAttempts) {
        setPollingAttempts(attempt + 1);
        setTimeout(() => {
          checkPaymentStatus(sessionId, attempt + 1);
        }, pollingInterval);
      } else {
        // Max attempts reached, show timeout message
        setPaymentStatus({
          success: false,
          error: 'Payment status check timed out. Please check your email for confirmation.',
          status: status.status
        });
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus({
        success: false,
        error: 'Failed to verify payment status',
        details: error.response?.data?.detail || error.message
      });
      setLoading(false);
    }
  };

  const getPackageName = (amount) => {
    switch (amount) {
      case 5: return 'Single Job Package';
      case 20: return '5 Job Package';
      case 35: return '10 Job Package';
      case 99: return 'Unlimited Monthly Package';
      default: return 'Job Posting Package';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="payment-loading">
        <Card className="bg-white/5 backdrop-blur-md border border-white/5 max-w-md mx-auto">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-16 w-16 mx-auto mb-4 text-primary animate-spin" />
            <h2 className="text-xl font-semibold mb-2">Verifying Payment</h2>
            <p className="text-muted-foreground mb-4">
              Please wait while we confirm your payment...
            </p>
            <div className="text-sm text-muted-foreground">
              Attempt {pollingAttempts + 1} of {maxPollingAttempts + 1}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="payment-success-page">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-2 font-['Barlow_Condensed']">
          Payment Status
        </h1>
      </div>

      {/* Payment Status Card */}
      <Card className="bg-white/5 backdrop-blur-md border border-white/5 max-w-2xl mx-auto mb-8">
        <CardContent className="p-8 text-center">
          {paymentStatus?.success ? (
            /* Success State */
            <>
              <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" data-testid="success-icon" />
              <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
              <p className="text-muted-foreground mb-6">
                Thank you for your purchase. Your payment has been processed successfully.
              </p>
              
              {/* Purchase Details */}
              <div className="bg-white/10 rounded-lg p-6 mb-6 text-left">
                <h3 className="font-semibold text-white mb-4">Purchase Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Package:</span>
                    <span className="text-white">{getPackageName(paymentStatus.amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount:</span>
                    <span className="text-white font-semibold">
                      {paymentStatus.amount.toLocaleString('en-US', {
                        style: 'currency',
                        currency: paymentStatus.currency
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <span className="text-green-400 capitalize">{paymentStatus.payment_status}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Session ID:</span>
                    <span className="text-white font-mono text-xs">{sessionId}</span>
                  </div>
                </div>
              </div>
              
              {/* Next Steps */}
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-primary mb-2">What&apos;s Next?</h4>
                <ul className="text-sm text-muted-foreground text-left space-y-1">
                  <li>• You can now post jobs using your purchased credits</li>
                  <li>• Access advanced applicant filtering features</li>
                  <li>• Your job postings will receive priority placement</li>
                  <li>• A receipt has been sent to your email address</li>
                </ul>
              </div>
            </>
          ) : (
            /* Error State */
            <>
              <AlertCircle className="h-16 w-16 mx-auto mb-4 text-red-500" data-testid="error-icon" />
              <h2 className="text-2xl font-bold text-white mb-2">Payment Issue</h2>
              <p className="text-muted-foreground mb-6">
                {paymentStatus?.error || 'There was an issue processing your payment.'}
              </p>
              
              {/* Error Details */}
              {paymentStatus?.details && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
                  <p className="text-sm text-red-400">
                    {paymentStatus.details}
                  </p>
                </div>
              )}
              
              {sessionId && (
                <div className="bg-white/10 rounded-lg p-4 mb-6 text-left">
                  <h4 className="font-semibold text-white mb-2">Session Information</h4>
                  <div className="text-sm">
                    <div className="flex justify-between mb-1">
                      <span className="text-muted-foreground">Session ID:</span>
                      <span className="text-white font-mono text-xs">{sessionId}</span>
                    </div>
                    {paymentStatus?.status && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <span className="text-white capitalize">{paymentStatus.status}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
        <Button 
          asChild 
          className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))] flex-1"
          data-testid="dashboard-btn"
        >
          <Link to="/dashboard">
            <Home className="mr-2 h-4 w-4" />
            Go to Dashboard
          </Link>
        </Button>
        
        {paymentStatus?.success ? (
          <Button 
            asChild 
            variant="outline" 
            className="flex-1"
            data-testid="post-job-btn"
          >
            <Link to="/jobs">
              <Briefcase className="mr-2 h-4 w-4" />
              Post Your First Job
            </Link>
          </Button>
        ) : (
          <Button 
            asChild 
            variant="outline" 
            className="flex-1"
            data-testid="try-again-btn"
          >
            <Link to="/pricing">
              <CreditCard className="mr-2 h-4 w-4" />
              Try Again
            </Link>
          </Button>
        )}
      </div>
      
      {/* Support Note */}
      <div className="text-center mt-8">
        <p className="text-sm text-muted-foreground">
          Need help? Contact our support team for assistance.
        </p>
      </div>
    </div>
  );
}

export default PaymentSuccessPage;
